import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Plus, PowerOff, RotateCcw } from 'lucide-react';
import { api } from '@/lib/api';
import { rowsOf } from '@/Hooks/useApi';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { ToggleRow } from '@/components/ui/switch';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TD, TH, THead, TR, TableEmpty, TableWrap } from '@/components/ui/table';
import { apiMessage, type BranchOption } from '../shared';
import {
  EMPTY_WAREHOUSE,
  branchLabel,
  warehouseCreateBody,
  warehouseProblem,
  warehouseUpdateBody,
  type WarehouseDraft,
  type WarehouseRow,
} from './inventoryDefs';

/** `null` is "create a new one"; a row is "edit this one". */
export type WarehouseTarget = WarehouseRow | 'new';

interface PanelProps {
  warehouses: WarehouseRow[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  mayWrite: boolean;
  onEdit: (target: WarehouseTarget) => void;
  onChanged: () => void;
}

/**
 * X-16 — the stock locations tab.
 *
 * `/inventory/warehouses` has had POST, PATCH and DELETE since P11-02 and no
 * client has ever called one, which is how demo-cafe ended up with a single
 * `isActive: false` warehouse and a transfer button that refused to open. The
 * table therefore leads with the two things that were unreachable: making one,
 * and bringing a retired one back.
 */
export function WarehousesPanel({
  warehouses,
  loading,
  error,
  onRetry,
  mayWrite,
  onEdit,
  onChanged,
}: PanelProps) {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const [busyId, setBusyId] = useState<string | null>(null);

  const active = warehouses.filter((w) => w.isActive).length;

  /**
   * `DELETE /inventory/warehouses/:id` is a deactivation — `WarehousesService`
   * writes `isActive: false, isDefault: false` and nothing else. It does NOT
   * refuse a warehouse that holds stock, and the balances it holds stay in the
   * ledger and stay counted by `/inventory/reports/valuation`. So the ask is
   * about what actually happens, and it names the balances rather than warning
   * about a refusal that will not come.
   */
  const retire = async (warehouse: WarehouseRow) => {
    let held: { onHand: string; value: string }[] = [];
    try {
      const { data } = await api.get('/stock-movements/levels', {
        params: { warehouseId: warehouse.id },
      });
      held = rowsOf<{ onHand: string; value: string }>(data).rows.filter(
        (row) => Number(row.onHand) !== 0,
      );
    } catch {
      // A balance we could not read is not a reason to refuse to ask.
      held = [];
    }

    const ok = await confirm({
      title: t('operations.inventory.defs.warehouses.retireTitle', { name: warehouse.name }),
      body: held.length
        ? t('operations.inventory.defs.warehouses.retireBodyStock', { count: held.length })
        : t('operations.inventory.defs.warehouses.retireBody'),
      confirmLabel: t('operations.inventory.defs.warehouses.retire'),
      tone: 'danger',
    });
    if (!ok) return;

    setBusyId(warehouse.id);
    try {
      await api.delete(`/inventory/warehouses/${warehouse.id}`);
      toast.success(t('operations.inventory.defs.warehouses.retired', { name: warehouse.name }));
      onChanged();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.inventory.defs.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  /** The way back out of demo-cafe's dead end: one tap on the retired store. */
  const reactivate = async (warehouse: WarehouseRow) => {
    setBusyId(warehouse.id);
    try {
      await api.patch(`/inventory/warehouses/${warehouse.id}`, { isActive: true });
      toast.success(
        t('operations.inventory.defs.warehouses.reactivated', { name: warehouse.name }),
      );
      onChanged();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.inventory.defs.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  if (error) return <ErrorState message={error} onRetry={onRetry} />;
  if (loading && !warehouses.length) return <LoadingState label={t('common.loading')} />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-muted">
          {t('operations.inventory.defs.warehouses.summary', {
            active,
            total: warehouses.length,
          })}
        </p>
        {mayWrite && (
          <Button onClick={() => onEdit('new')}>
            <Plus className="h-4 w-4" />
            {t('operations.inventory.defs.warehouses.new')}
          </Button>
        )}
      </div>

      <TableWrap>
        <Table>
          <THead>
            <TR>
              <TH>{t('operations.inventory.defs.warehouses.name')}</TH>
              <TH>{t('operations.inventory.defs.warehouses.code')}</TH>
              <TH>{t('operations.inventory.defs.warehouses.branch')}</TH>
              <TH>{t('common.status')}</TH>
              {mayWrite && <TH className="text-end">{t('operations.common.actions')}</TH>}
            </TR>
          </THead>
          <tbody>
            {!warehouses.length ? (
              <TableEmpty colSpan={mayWrite ? 5 : 4}>
                {t('operations.inventory.defs.warehouses.none')}
              </TableEmpty>
            ) : (
              warehouses.map((warehouse) => {
                const label = branchLabel(warehouse);
                return (
                  <TR key={warehouse.id}>
                    <TD className="font-medium">{warehouse.name}</TD>
                    <TD className="font-mono text-xs">{warehouse.code}</TD>
                    <TD>
                      {label.kind === 'tenant-wide' ? (
                        <span className="text-ink-muted">
                          {t('operations.inventory.defs.warehouses.tenantWide')}
                        </span>
                      ) : label.kind === 'named' ? (
                        label.name
                      ) : (
                        // Not a blank and not "tenant-wide" — a branch this
                        // tenant cannot read. Saying so is the only way the
                        // operator ever learns RTWH1 is misanchored.
                        <Badge tone="warning">
                          {t('operations.inventory.defs.warehouses.unreadableBranch')}
                        </Badge>
                      )}
                    </TD>
                    <TD>
                      <div className="flex flex-wrap gap-1.5">
                        {warehouse.isDefault && (
                          <Badge tone="success">
                            {t('operations.inventory.defs.warehouses.default')}
                          </Badge>
                        )}
                        <Badge tone={warehouse.isActive ? 'neutral' : 'danger'}>
                          {t(warehouse.isActive ? 'operations.inventory.defs.active' : 'operations.inventory.defs.inactive')}
                        </Badge>
                      </div>
                    </TD>
                    {mayWrite && (
                      <TD className="text-end">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            aria-label={t('common.edit')}
                            title={t('common.edit')}
                            disabled={busyId === warehouse.id}
                            onClick={() => onEdit(warehouse)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {warehouse.isActive ? (
                            <Button
                              variant="outline"
                              size="icon"
                              aria-label={t('operations.inventory.defs.warehouses.retire')}
                              title={t('operations.inventory.defs.warehouses.retire')}
                              disabled={busyId === warehouse.id}
                              onClick={() => retire(warehouse)}
                            >
                              <PowerOff className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              size="icon"
                              aria-label={t('operations.inventory.defs.warehouses.reactivate')}
                              title={t('operations.inventory.defs.warehouses.reactivate')}
                              disabled={busyId === warehouse.id}
                              onClick={() => reactivate(warehouse)}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TD>
                    )}
                  </TR>
                );
              })
            )}
          </tbody>
        </Table>
      </TableWrap>
    </div>
  );
}

interface DialogProps {
  target: WarehouseTarget | null;
  branches: BranchOption[];
  onClose: () => void;
  onSaved: (warehouse: WarehouseRow) => void;
}

export function WarehouseDialog({ target, branches, onClose, onSaved }: DialogProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<WarehouseDraft>(EMPTY_WAREHOUSE);
  const [busy, setBusy] = useState(false);
  const editing = target && target !== 'new' ? target : null;

  useEffect(() => {
    if (!target) return;
    setDraft(
      target === 'new'
        ? EMPTY_WAREHOUSE
        : {
            name: target.name,
            code: target.code,
            branchId: target.branchId ?? '',
            isDefault: target.isDefault,
            isActive: target.isActive,
          },
    );
  }, [target]);

  const problem = useMemo(() => warehouseProblem(draft), [draft]);

  const submit = async () => {
    if (problem) return;
    setBusy(true);
    try {
      const { data } = editing
        ? await api.patch(`/inventory/warehouses/${editing.id}`, warehouseUpdateBody(draft))
        : await api.post('/inventory/warehouses', warehouseCreateBody(draft));
      const saved = ((data as { data?: WarehouseRow }).data ?? data) as WarehouseRow;
      toast.success(
        t(
          editing
            ? 'operations.inventory.defs.warehouses.updated'
            : 'operations.inventory.defs.warehouses.created',
          { name: draft.name.trim() },
        ),
      );
      onSaved(saved);
    } catch (e) {
      toast.error(apiMessage(e, t('operations.inventory.defs.saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const label = (field: 'name' | 'code') =>
    problem?.field === field ? t('operations.inventory.defs.required') : undefined;

  return (
    <Dialog
      open={!!target}
      onClose={onClose}
      title={t(
        editing
          ? 'operations.inventory.defs.warehouses.edit'
          : 'operations.inventory.defs.warehouses.new',
      )}
      description={t('operations.inventory.defs.warehouses.formHint')}
      className="max-w-2xl"
      footer={
        <>
          <Button variant="ghost" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" onClick={submit} disabled={busy || !!problem}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t('operations.inventory.defs.warehouses.name')}
          required
          error={label('name')}
        >
          <Input
            autoFocus
            className="h-12"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder={t('operations.inventory.defs.warehouses.namePlaceholder')}
          />
        </Field>

        <Field
          label={t('operations.inventory.defs.warehouses.code')}
          required
          error={label('code')}
          hint={t('operations.inventory.defs.warehouses.codeHint')}
        >
          <Input
            className="h-12 font-mono"
            value={draft.code}
            onChange={(e) => setDraft({ ...draft, code: e.target.value })}
            placeholder="WH-MAIN"
          />
        </Field>

        {/* `UpdateWarehouseDto` omits `branchId` — the anchor is set once. An
            editable control here could only ever earn a 400. */}
        <Field
          label={t('operations.inventory.defs.warehouses.branch')}
          hint={t(
            editing
              ? 'operations.inventory.defs.warehouses.branchLocked'
              : 'operations.inventory.defs.warehouses.branchHint',
          )}
        >
          {editing ? (
            <div className="flex h-12 items-center rounded-lg border border-line bg-subtle px-3 text-sm">
              {branchLabel(editing).kind === 'tenant-wide'
                ? t('operations.inventory.defs.warehouses.tenantWide')
                : branchLabel(editing).kind === 'named'
                  ? (editing.branch?.name ?? '')
                  : t('operations.inventory.defs.warehouses.unreadableBranch')}
            </div>
          ) : (
            <Select
              className="h-12"
              value={draft.branchId}
              onChange={(e) => setDraft({ ...draft, branchId: e.target.value })}
            >
              <option value="">{t('operations.inventory.defs.warehouses.tenantWide')}</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <div className="space-y-3 sm:col-span-2">
          <ToggleRow
            checked={draft.isDefault}
            onChange={(isDefault) => setDraft({ ...draft, isDefault })}
            label={t('operations.inventory.defs.warehouses.isDefault')}
            hint={t('operations.inventory.defs.warehouses.isDefaultHint')}
          />
          <ToggleRow
            checked={draft.isActive}
            onChange={(isActive) => setDraft({ ...draft, isActive })}
            label={t('operations.inventory.defs.active')}
            hint={t('operations.inventory.defs.warehouses.isActiveHint')}
          />
        </div>
      </div>
    </Dialog>
  );
}
